import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import useSWR from "swr";
import axios from "axios";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Typography } from "@material-tailwind/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowsAlt,
  FaEdit,
  FaFileAlt,
  FaFileArchive,
  FaFilePdf,
  FaFileWord,
  FaPlayCircle,
  FaPlusCircle,
  FaTimes,
  FaTrashAlt,
  FaUpload,
  FaVideo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import { ASSET_URI, PROXY, fetchData, limitCharecters } from "../lib/utils";
import { getLanguageDirection } from "../i18n/config";
import { generateLessonTitles } from "../lib/lessonTitleUtils";

const cardClassName = "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm";
// Material Tailwind's default Button colors can override a local background
// without updating its foreground. These explicit pairs keep every action on
// this page legible in normal, hover, focus, active, disabled and loading states.
const primaryButtonClass = "rounded-lg !bg-indigo-700 !text-white hover:!bg-indigo-800 active:!bg-indigo-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 disabled:!bg-indigo-200 disabled:!text-indigo-800";
const outlineButtonClass = "rounded-lg !border-indigo-300 !bg-white !text-indigo-800 hover:!border-indigo-500 hover:!bg-indigo-50 active:!bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 disabled:!border-slate-200 disabled:!bg-slate-100 disabled:!text-slate-400";
const dangerButtonClass = "rounded-lg !bg-red-700 !text-white hover:!bg-red-800 active:!bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:!bg-red-200 disabled:!text-red-700";
const textButtonClass = "rounded-lg !bg-transparent !text-indigo-700 hover:!bg-indigo-50 hover:!text-indigo-950 active:!bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 disabled:!text-slate-400";
const MAX_VIDEO_UPLOAD_MB = 2048;
const MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo"];

const descriptionSchema = (label, t) =>
  yup.object({
    value: yup
      .string()
      .trim(),
  });

const addChapterSchema = (t) => yup.object({
  value: yup
    .string()
    .trim()
    .min(3, t("teacherContent.validation_video_title_min", "Video title must be at least 3 characters"))
    .required(t("teacherContent.validation_video_title_required", "Video title is required")),
});

const videoSchema = (t) => yup.object({
  video: yup
    .mixed()
    .required(t("teacherContent.validation_video_required", "Video is required"))
    .test("fileFormat", t("teacherContent.validation_video_format", "Unsupported format"), (value) => {
      return value && ALLOWED_VIDEO_TYPES.includes(value.type);
    })
    .test("fileSize", t("teacherContent.validation_video_size", { size: MAX_VIDEO_UPLOAD_MB, defaultValue: `Video must be ${MAX_VIDEO_UPLOAD_MB}MB or smaller` }), (value) => {
      return value && value.size <= MAX_VIDEO_UPLOAD_BYTES;
    }),
});

const getStatusLabel = (status, t) => {
  if (status === "published") {
    return t("teacherContent.status_published", "Published");
  }

  return t("teacherContent.status_draft", "Draft");
};

const getCourseTitle = (course, t) =>
  course?.title?.en || course?.title?.pr || course?.title?.ps || t("teacherContent.untitled_course", "Untitled course");

const getFileIcon = (filePath = "", mimeType = "") => {
  const ext = filePath.split(".").pop()?.toLowerCase();
  const type = mimeType.toLowerCase();

  if (ext === "pdf" || type.includes("pdf")) {
    return <FaFilePdf className="text-red-600" size={14} />;
  }
  if (ext === "doc" || ext === "docx" || type.includes("word")) {
    return <FaFileWord className="text-blue-600" size={14} />;
  }
  if (ext === "zip" || type.includes("zip")) {
    return <FaFileArchive className="text-amber-600" size={14} />;
  }
  return <FaFileAlt className="text-slate-500" size={14} />;
};

const formatSize = (size = 0) => {
  if (!size) return "-";
  const mb = size / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.round(size / 1024)} KB`;
};

const statusTone = (status) => (status === "published" ? "green" : "orange");

const Modal = ({ open, onClose, className = "max-w-5xl", children }) => {
  const { t, i18n } = useTranslation();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/60"
        onClick={onClose}
        aria-label={t("teacherContent.close_modal", "Close modal")}
      />
      <div className="relative flex min-h-full items-center justify-center px-2 py-4 sm:px-4 sm:py-8">
        <div
          dir={getLanguageDirection(i18n.language)}
          className={`relative flex w-full flex-col rounded-3xl bg-white shadow-xl ${className}`}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const LessonTitlesEditor = ({ courseId, chapter, accessToken, mutate }) => {
  const [lessonNumber, setLessonNumber] = useState(chapter?.lessonNumber ?? "");
  const [partNumber, setPartNumber] = useState(chapter?.partNumber ?? "");
  const [titles, setTitles] = useState(() => ({
    ps: chapter?.title?.ps || "",
    pr: chapter?.title?.pr || "",
    en: chapter?.title?.en || "",
  }));
  const [isSaving, setIsSaving] = useState(false);
  const { t, i18n } = useTranslation();

  // Do not generate here: an existing localized title is authoritative until a
  // teacher changes one of the number fields.
  useEffect(() => {
    setLessonNumber(chapter?.lessonNumber ?? "");
    setPartNumber(chapter?.partNumber ?? "");
    setTitles({
      ps: chapter?.title?.ps || "",
      pr: chapter?.title?.pr || "",
      en: chapter?.title?.en || "",
    });
  }, [chapter]);

  const setNumberAndGenerate = (nextLesson, nextPart) => {
    setLessonNumber(nextLesson);
    setPartNumber(nextPart);
    const generated = generateLessonTitles(nextLesson, nextPart);
    if (generated) setTitles(generated);
  };

  const save = async () => {
    const lesson = String(lessonNumber).trim();
    const part = String(partNumber).trim();
    const isIntroduction = lesson === "0" && part === "0";
    const lessonIsValid = /^([1-9]|[1-9]\d)$/.test(lesson);
    const partIsValid = part === "" || /^([1-9]|[1-9]\d)$/.test(part);
    if ((lesson !== "" || part !== "") && !isIntroduction && (!lessonIsValid || !partIsValid)) {
      toast.error(t("teacherContent.lesson_number_validation", "Use 1–99 for lesson and part numbers, or 0/0 for the course introduction."));
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch(`${PROXY}/teacher/courses/${courseId}/chapter/${chapter?._id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: titles,
          ...(lesson !== "" ? { lessonNumber: Number(lesson) } : {}),
          ...(part !== "" ? { partNumber: Number(part) } : {}),
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.message || "Failed to update video titles.");
      toast.success(t("teacherContent.toast_title_updated", { label: t("teacherContent.video_titles", "Video titles"), defaultValue: "Video titles updated successfully." }));
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_title_update_failed", "Failed to update video titles."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div dir={getLanguageDirection(i18n.language)} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm font-semibold text-slate-900">
          {t("teacherContent.lesson_number", "Lesson Number")}
          <input type="number" min="0" max="99" value={lessonNumber} onChange={(event) => setNumberAndGenerate(event.target.value, partNumber)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-normal" />
        </label>
        <label className="text-sm font-semibold text-slate-900">
          {t("teacherContent.part_number", "Part Number")}
          <input type="number" min="0" max="99" value={partNumber} onChange={(event) => setNumberAndGenerate(lessonNumber, event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-normal" />
        </label>
      </div>
      {["ps", "pr", "en"].map((lang) => (
        <label key={lang} className="block text-sm font-semibold text-slate-900">
          {lang.toUpperCase()} {t("teacherContent.title", "Title")}
          <input dir={lang === "en" ? "ltr" : "rtl"} value={titles[lang]} onChange={(event) => setTitles((current) => ({ ...current, [lang]: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-normal" />
        </label>
      ))}
      <Button type="button" size="sm" disabled={isSaving} onClick={save} className={primaryButtonClass}>
        {isSaving ? t("teacherContent.saving", "Saving...") : t("teacherContent.save", "Save")}
      </Button>
    </div>
  );
};

LessonTitlesEditor.propTypes = {
  courseId: PropTypes.string.isRequired,
  chapter: PropTypes.object.isRequired,
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

const InlineDescriptionEditor = ({
  courseId,
  chapterId,
  label,
  lang,
  initialValue,
  accessToken,
  mutate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { t, i18n } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(descriptionSchema(label, t)),
    defaultValues: { value: initialValue || "" },
  });

  useEffect(() => {
    reset({ value: initialValue || "" });
  }, [initialValue, reset]);

  const onSubmit = async (data) => {
    try {
      const response = await fetch(`${PROXY}/teacher/courses/${courseId}/chapter/${chapterId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          desc: {
            [lang]: data.value.trim(),
          },
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || `Failed to update ${label}.`);
      }

      toast.success(t("teacherContent.toast_title_updated", { label, defaultValue: `${label} updated successfully.` }));
      setIsEditing(false);
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_title_update_failed", { label, defaultValue: `Failed to update ${label}.` }));
    }
  };

  return (
    <div dir={getLanguageDirection(i18n.language)} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <label className="block text-sm font-semibold text-slate-900">{label}</label>
          <textarea
            {...register("value")}
            rows={6}
            className="min-h-[9rem] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
          />
          {errors.value ? <p className="text-xs text-red-500">{errors.value.message}</p> : null}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={isSubmitting} className={primaryButtonClass}>
              {isSubmitting ? t("teacherContent.saving", "Saving...") : t("teacherContent.save", "Save")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outlined"
              color="blue-gray"
              className={outlineButtonClass}
              onClick={() => {
                reset({ value: initialValue || "" });
                setIsEditing(false);
              }}
            >
              {t("teacherContent.cancel", "Cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900">{label}</p>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50 hover:text-indigo-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
            >
              <FaEdit size={12} />
              {t("teacherContent.edit", "Edit")}
            </button>
          </div>
          <p className="text-sm text-slate-600">{limitCharecters(initialValue || "", 250) || t("teacherContent.no_value", { label: label.toLowerCase(), defaultValue: `No ${label.toLowerCase()}` })}</p>
        </div>
      )}
    </div>
  );
};

InlineDescriptionEditor.propTypes = {
  courseId: PropTypes.string.isRequired,
  chapterId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired,
  initialValue: PropTypes.string,
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

const ChapterVideoUploader = ({
  courseId,
  chapterId,
  slug,
  initialValue,
  accessToken,
  mutate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const { t, i18n } = useTranslation();
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(videoSchema(t)),
    defaultValues: {
      video: null,
    },
  });

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);
    setValue("video", file, { shouldValidate: true });
  };

  const onSubmit = async ({ video }) => {
    const formData = new FormData();
    formData.append("video", video);

    try {
      await axios.post(
        `${PROXY}/teacher/courses/${courseId}/chapter/${chapterId}/upload?slug=${slug || ""}&video=${initialValue || ""}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress(percent);
          },
        },
      );

      toast.success(t("teacherContent.toast_video_uploaded", "Video uploaded successfully."));
      setUploadProgress(0);
      setIsEditing(false);
      reset({ video: null });
      mutate();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || t("teacherContent.toast_video_upload_failed", "Failed to upload video.");
      toast.error(message);
      setUploadProgress(0);
    }
  };

  return (
    <div dir={getLanguageDirection(i18n.language)} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {previewUrl ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-black">
              <video src={previewUrl} controls className="h-64 w-full object-contain" />
            </div>
          ) : (
            <label className={`flex h-56 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-white px-4 text-center ${
              errors.video ? "border-red-500" : "border-slate-300"
            }`}>
              <FaUpload className="mb-3 text-slate-400" size={24} />
              <p className="text-sm font-semibold text-slate-800">{t("teacherContent.click_to_upload_video", "Click to upload course video")}</p>
              <p className="mt-1 text-xs text-slate-500">{t("teacherContent.video_upload_formats", { size: MAX_VIDEO_UPLOAD_MB, defaultValue: `MP4, MOV, AVI up to ${MAX_VIDEO_UPLOAD_MB}MB` })}</p>
              <input
                type="file"
                accept=".mp4,.mov,.avi,video/mp4,video/quicktime,video/x-msvideo"
                className="hidden"
                onChange={handleVideoChange}
              />
            </label>
          )}

          {uploadProgress > 0 ? (
            <div className="space-y-2">
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-indigo-600" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-slate-500">
                {t("teacherContent.upload_progress", {
                  progress: uploadProgress,
                  defaultValue: "{{progress}}% uploaded",
                })}
              </p>
            </div>
          ) : null}

          {errors.video ? <p className="text-xs text-red-500">{errors.video.message}</p> : null}

          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={isSubmitting} className={primaryButtonClass}>
              {isSubmitting ? t("teacherContent.uploading", "Uploading...") : t("teacherContent.update_video", "Update video")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outlined"
              color="blue-gray"
              className={outlineButtonClass}
              onClick={() => {
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setPreviewUrl("");
                setIsEditing(false);
                setUploadProgress(0);
                reset({ video: null });
              }}
            >
              {t("teacherContent.cancel", "Cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900">{t("teacherContent.video_file", "Video file")}</p>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50 hover:text-indigo-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
            >
              <FaEdit size={12} />
              {t("teacherContent.edit", "Edit")}
            </button>
          </div>
          {initialValue ? (
            <video
              src={`${PROXY}/courses/stream/video?filePath=${encodeURIComponent(initialValue)}`}
              controls
              className="h-64 w-full rounded-2xl bg-black object-contain"
              preload="metadata"
            />
          ) : (
            <p className="text-sm text-slate-600">{t("teacherContent.no_video_uploaded", "No video uploaded yet.")}</p>
          )}
        </div>
      )}
    </div>
  );
};

ChapterVideoUploader.propTypes = {
  courseId: PropTypes.string.isRequired,
  chapterId: PropTypes.string.isRequired,
  slug: PropTypes.string,
  initialValue: PropTypes.string,
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

const ChapterEditor = ({ courseId, chapter, slug, accessToken, mutate, onClose }) => {
  const [pendingAction, setPendingAction] = useState("");
  const { t, i18n } = useTranslation();

  const deleteChapter = async () => {
    try {
      setPendingAction("delete");
      const response = await fetch(`${PROXY}/teacher/courses/${courseId}/chapter/${chapter?._id}?source=${chapter?.video || ""}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_video_delete_failed", "Failed to delete video."));
      }

      toast.success(t("teacherContent.toast_video_deleted", "Video deleted successfully."));
      onClose();
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_video_delete_failed", "Failed to delete video."));
    } finally {
      setPendingAction("");
    }
  };

  return (
    <section dir={getLanguageDirection(i18n.language)} className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Button
            type="button"
            variant="text"
            onClick={onClose}
            className={`mb-2 flex items-center gap-2 px-2 text-sm font-medium ${textButtonClass}`}
          >
            <FaArrowLeft size={12} />
            {t("teacherContent.back_to_videos", "Back to videos")}
          </Button>
          <h3 className="text-xl font-semibold text-slate-900">{t("teacherContent.video_details_title", "Video details")}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {t("teacherContent.video_details_description", "Edit titles, descriptions, upload the video file, and manage publishing from one place.")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Chip
            value={getStatusLabel(chapter?.status, t)}
            color={chapter?.status === "published" ? "green" : "orange"}
            variant="ghost"
            className="capitalize"
          />
          <Button
            type="button"
            size="sm"
            color="red"
            className={dangerButtonClass}
            disabled={pendingAction === "delete"}
            onClick={deleteChapter}
          >
            {pendingAction === "delete" ? t("teacherContent.deleting", "Deleting...") : t("teacherContent.delete_video", "Delete video")}
          </Button>
        </div>
      </div>

      <article className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_1fr]">
        <div className="space-y-4">
          <div className={cardClassName}>
            <div className="mb-4">
              <p className="text-sm font-semibold text-slate-900">{t("teacherContent.video_titles", "Video titles")}</p>
              <p className="text-xs text-slate-500">
                {t("teacherContent.video_titles_description", "Keep the video title consistent across all supported languages.")}
              </p>
            </div>
            <LessonTitlesEditor
              courseId={courseId}
              chapter={chapter}
              accessToken={accessToken}
              mutate={mutate}
            />
          </div>

          <div className={cardClassName}>
            <div className="mb-4">
              <p className="text-sm font-semibold text-slate-900">{t("teacherContent.descriptions", "Descriptions")}</p>
              <p className="text-xs text-slate-500">
                {t("teacherContent.descriptions_description", "Add learner-facing descriptions for the video in each language.")}
              </p>
            </div>
            <div className="space-y-4">
              <InlineDescriptionEditor
                courseId={courseId}
                chapterId={chapter?._id}
                label={t("teacherContent.pashto_description", "Pashto Description")}
                lang="ps"
                initialValue={chapter?.desc?.ps}
                accessToken={accessToken}
                mutate={mutate}
              />
              <InlineDescriptionEditor
                courseId={courseId}
                chapterId={chapter?._id}
                label={t("teacherContent.dari_description", "Dari Description")}
                lang="pr"
                initialValue={chapter?.desc?.pr}
                accessToken={accessToken}
                mutate={mutate}
              />
              <InlineDescriptionEditor
                courseId={courseId}
                chapterId={chapter?._id}
                label={t("teacherContent.english_description", "English Description")}
                lang="en"
                initialValue={chapter?.desc?.en}
                accessToken={accessToken}
                mutate={mutate}
              />
            </div>
          </div>
        </div>

        <div className={cardClassName}>
          <div className="mb-4">
              <p className="text-sm font-semibold text-slate-900">{t("teacherContent.video_file", "Video file")}</p>
            <p className="text-xs text-slate-500">
                {t("teacherContent.video_file_description", "Upload or replace the lesson video file for this item.")}
            </p>
          </div>
          <ChapterVideoUploader
            courseId={courseId}
            chapterId={chapter?._id}
            slug={slug}
            initialValue={chapter?.video}
            accessToken={accessToken}
            mutate={mutate}
          />
        </div>
      </article>
      <div className={cardClassName}>
        <CourseFilesManager courseId={courseId} chapterId={chapter?._id} files={chapter?.files || []} accessToken={accessToken} mutate={mutate} />
      </div>
    </section>
  );
};

ChapterEditor.propTypes = {
  courseId: PropTypes.string.isRequired,
  chapter: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.object,
    desc: PropTypes.object,
    files: PropTypes.array,
    status: PropTypes.string,
    video: PropTypes.string,
  }),
  slug: PropTypes.string,
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const CourseFilesManager = ({ courseId, chapterId, files, accessToken, mutate }) => {
  const { t, i18n } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [pendingId, setPendingId] = useState("");
  const [uploadState, setUploadState] = useState({ title: "", file: null });
  const [editState, setEditState] = useState({});
  const fileRoute = `${PROXY}/teacher/courses/${courseId}${chapterId ? `/chapter/${chapterId}` : ""}/files`;

  const sortedFiles = useMemo(
    () => [...files].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [files],
  );

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!uploadState.file) {
      toast.error(t("teacherContent.toast_select_file_first", "Please select a file first."));
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadState.file);
    formData.append("title", uploadState.title.trim() || uploadState.file.name);

    try {
      setUploading(true);
      const response = await fetch(`${fileRoute}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_file_upload_failed", "Upload failed"));
      }

      toast.success(t("teacherContent.toast_file_upload_success", "Course file uploaded successfully."));
      setUploadState({ title: "", file: null });
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_file_upload_failed", "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (fileId) => {
    const draft = editState[fileId];
    if (!draft) return;

    const formData = new FormData();
    formData.append("title", draft.title?.trim() || t("teacherContent.untitled_file", "Untitled file"));
    if (draft.file) {
      formData.append("file", draft.file);
    }

    try {
      setPendingId(fileId);
      const response = await fetch(`${fileRoute}/${fileId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_file_update_failed", "Update failed"));
      }

      toast.success(t("teacherContent.toast_file_update_success", "Course file updated successfully."));
      setEditState((prev) => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_file_update_failed", "Update failed"));
    } finally {
      setPendingId("");
    }
  };

  const handleDelete = async (fileId) => {
    try {
      setPendingId(fileId);
      const response = await fetch(`${fileRoute}/${fileId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_file_delete_failed", "Delete failed"));
      }

      toast.success(t("teacherContent.toast_file_delete_success", "Course file deleted successfully."));
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_file_delete_failed", "Delete failed"));
    } finally {
      setPendingId("");
    }
  };

  return (
    <div dir={getLanguageDirection(i18n.language)} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{chapterId ? t("teacherContent.lesson_files_title", "Lesson files") : t("teacherContent.course_files_title", "Course files")}</p>
            <p className="text-xs text-slate-500">
              {chapterId ? t("teacherContent.lesson_files_description", "Attach optional files to this lesson.") : t("teacherContent.course_files_description", "Upload supporting files separately from videos and keep them available for learners.")}
            </p>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
            {t("teacherContent.file_count", {
              count: sortedFiles.length,
              defaultValue_one: "{{count}} file",
              defaultValue_other: "{{count}} files",
            })}
          </span>
        </div>

        <form onSubmit={handleUpload} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <div>
              <label className="text-sm font-medium text-slate-700">{t("teacherContent.file_title", "File title")}</label>
              <input
                type="text"
                value={uploadState.title}
                onChange={(event) => setUploadState((prev) => ({ ...prev, title: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
                placeholder={t("teacherContent.file_title", "File title")}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("teacherContent.upload_file_label", "Upload file")}</label>
              <input
                type="file"
                accept={chapterId ? ".pdf,.docx" : ".pdf,.doc,.docx,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/x-zip-compressed"}
                onChange={(event) =>
                  setUploadState((prev) => ({
                    ...prev,
                    file: event.target.files?.[0] || null,
                    title: prev.title || event.target.files?.[0]?.name || "",
                  }))
                }
                className="mt-2 block w-full text-sm text-slate-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
              />
            </div>
            <Button type="submit" disabled={uploading} className={primaryButtonClass}>
              {uploading ? t("teacherContent.uploading", "Uploading...") : t("teacherContent.upload_file", "Upload file")}
            </Button>
          </div>
        </form>

        {sortedFiles.length ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left">{t("teacherContent.title", "Title")}</th>
                    <th className="px-4 py-3 text-left">{t("teacherContent.file", "File")}</th>
                    <th className="px-4 py-3 text-left">{t("teacherContent.size", "Size")}</th>
                    <th className="px-4 py-3 text-left">{t("teacherContent.uploaded", "Uploaded")}</th>
                    <th className="px-4 py-3 text-right">{t("teacherContent.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map((file) => {
                    const draft = editState[file._id];
                    const isEditing = Boolean(draft);
                    return (
                      <tr key={file._id} className="border-t border-slate-100">
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={draft.title}
                                onChange={(event) =>
                                  setEditState((prev) => ({
                                    ...prev,
                                    [file._id]: {
                                      ...prev[file._id],
                                      title: event.target.value,
                                    },
                                  }))
                                }
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
                              />
                              <input
                                type="file"
                                accept={chapterId ? ".pdf,.docx" : ".pdf,.doc,.docx,.zip"}
                                onChange={(event) =>
                                  setEditState((prev) => ({
                                    ...prev,
                                    [file._id]: {
                                      ...prev[file._id],
                                      file: event.target.files?.[0] || null,
                                    },
                                  }))
                                }
                                className="block w-full text-xs text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
                              />
                            </div>
                          ) : (
                            <p className="font-semibold text-slate-900">{file.title}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {chapterId ? (
                            <span className="inline-flex items-center gap-2 text-slate-700">{getFileIcon(file.filePath, file.mimeType)}{file.originalName || file.title}</span>
                          ) : (
                            <a href={`${ASSET_URI}/${file.filePath}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-indigo-600 hover:underline">
                              {getFileIcon(file.filePath, file.mimeType)}
                              {file.originalName || t("teacherContent.download", "Download")}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatSize(file.size)}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleUpdate(file._id)}
                                  disabled={pendingId === file._id}
                                  className={`px-3 py-2 text-xs font-semibold ${primaryButtonClass}`}
                                >
                                  {pendingId === file._id ? t("teacherContent.saving", "Saving...") : t("teacherContent.save", "Save")}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outlined"
                                  color="blue-gray"
                                  className={outlineButtonClass}
                                  onClick={() =>
                                    setEditState((prev) => {
                                      const next = { ...prev };
                                      delete next[file._id];
                                      return next;
                                    })
                                  }
                                >
                                  {t("teacherContent.cancel", "Cancel")}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outlined"
                                  color="blue-gray"
                                  className={outlineButtonClass}
                                  onClick={() =>
                                    setEditState((prev) => ({
                                      ...prev,
                                      [file._id]: {
                                        title: file.title,
                                        file: null,
                                      },
                                    }))
                                  }
                                >
                                  {t("teacherContent.edit", "Edit")}
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(file._id)}
                                  disabled={pendingId === file._id}
                                  className="inline-flex items-center gap-2 rounded-lg border border-red-700 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 active:bg-red-100 disabled:border-red-200 disabled:text-red-300"
                                >
                                  <FaTrashAlt size={12} />
                                  {pendingId === file._id ? t("teacherContent.deleting", "Deleting...") : t("teacherContent.delete", "Delete")}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <p className="text-sm font-semibold text-slate-900">{t("teacherContent.no_course_files", "No course files uploaded yet")}</p>
            <p className="mt-1 text-xs text-slate-500">
              {t("teacherContent.no_course_files_description", "Upload PDFs, DOC files, or ZIP materials here. They stay separate from course videos.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

CourseFilesManager.propTypes = {
  courseId: PropTypes.string.isRequired,
  chapterId: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.object),
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

const ChapterManager = ({ courseId, slug, chaptersList, accessToken, mutate }) => {
  const [chapters, setChapters] = useState(chaptersList || []);
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [creationTitles, setCreationTitles] = useState({ ps: "", pr: "", en: "" });
  const [creationDescriptions, setCreationDescriptions] = useState({ ps: "", pr: "", en: "" });
  const [creationFile, setCreationFile] = useState(null);
  const [lessonNumber, setLessonNumber] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [isOrdering, setIsOrdering] = useState(false);
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);
  const [draggedChapterId, setDraggedChapterId] = useState("");
  const { t, i18n } = useTranslation();
  const {
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: yupResolver(addChapterSchema(t)),
    defaultValues: { value: "" },
  });

  useEffect(() => {
    setChapters(chaptersList || []);
  }, [chaptersList]);

  const persistOrder = async (orderedChapters, fallbackChapters) => {
    try {
      setIsOrdering(true);
      const response = await fetch(`${PROXY}/teacher/courses/${courseId}/chapters/order`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderedChapterIds: orderedChapters.map((chapter) => chapter._id),
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_save_order_failed", "Failed to save video order."));
      }

      setChapters(orderedChapters);
      toast.success(
        t("teacherContent.toast_save_order_success", "Video order updated successfully."),
      );
      mutate();
    } catch (error) {
      setChapters(fallbackChapters);
      toast.error(
        error.message ||
          t("teacherContent.toast_save_order_failed", "Failed to save video order."),
      );
    } finally {
      setIsOrdering(false);
    }
  };

  const reorderChapters = (sourceIndex, destinationIndex) => {
    if (destinationIndex < 0 || destinationIndex >= chapters.length || sourceIndex === destinationIndex) {
      return;
    }

    const previousChapters = [...chapters];
    const reorderedChapters = [...chapters];
    const [chapter] = reorderedChapters.splice(sourceIndex, 1);
    reorderedChapters.splice(destinationIndex, 0, chapter);
    setChapters(reorderedChapters);
    persistOrder(reorderedChapters, previousChapters);
  };

  const handleDragStart = (chapterId) => {
    setDraggedChapterId(chapterId);
  };

  const handleDrop = (targetChapterId) => {
    if (!draggedChapterId || draggedChapterId === targetChapterId) {
      setDraggedChapterId("");
      return;
    }

    const sourceIndex = chapters.findIndex((chapter) => chapter._id === draggedChapterId);
    const destinationIndex = chapters.findIndex((chapter) => chapter._id === targetChapterId);

    if (sourceIndex === -1 || destinationIndex === -1) {
      setDraggedChapterId("");
      return;
    }

    reorderChapters(sourceIndex, destinationIndex);
    setDraggedChapterId("");
  };

  const onCreateChapter = async () => {
    const lesson = String(lessonNumber).trim();
    const part = String(partNumber).trim();
    const isIntroduction = lesson === "0" && part === "0";
    const lessonIsValid = /^([1-9]|[1-9]\d)$/.test(lesson);
    const partIsValid = part === "" || /^([1-9]|[1-9]\d)$/.test(part);
    if ((lesson !== "" || part !== "") && !isIntroduction && (!lessonIsValid || !partIsValid)) {
      toast.error(t("teacherContent.lesson_number_validation", "Use 1–99 for lesson and part numbers, or 0/0 for the course introduction."));
      return;
    }
    if ((creationTitles.en || "").trim().length < 3) {
      toast.error(t("teacherContent.validation_video_title_min", "Video title must be at least 3 characters"));
      return;
    }
    try {
      setIsCreatingLesson(true);
      const response = await fetch(`${PROXY}/teacher/courses/chapters/${courseId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: creationTitles,
          desc: creationDescriptions,
          ...(lessonNumber !== "" ? { lessonNumber: Number(lessonNumber) } : {}),
          ...(partNumber !== "" ? { partNumber: Number(partNumber) } : {}),
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("teacherContent.toast_create_video_failed", "Failed to create video."));
      }

      if (creationFile && payload?._id) {
        const formData = new FormData();
        formData.append("file", creationFile);
        formData.append("title", creationFile.name);
        try {
          const uploadResponse = await fetch(`${PROXY}/teacher/courses/${courseId}/chapter/${payload._id}/files/upload`, {
            method: "POST", headers: { Authorization: `Bearer ${accessToken}` }, body: formData,
          });
          if (!uploadResponse.ok) throw new Error("Upload failed");
        } catch {
          toast.error(t("teacherContent.lesson_file_retry", "Lesson created, but its file was not uploaded. Add it from the lesson editor."));
        }
      }

      toast.success(t("teacherContent.toast_create_video_success", "Video created successfully."));
      reset({ value: "" }); setCreationTitles({ ps: "", pr: "", en: "" }); setCreationDescriptions({ ps: "", pr: "", en: "" }); setCreationFile(null); setLessonNumber(""); setPartNumber("");
      setIsCreating(false);
      mutate();
    } catch (error) {
      toast.error(error.message || t("teacherContent.toast_create_video_failed", "Failed to create video."));
    } finally {
      setIsCreatingLesson(false);
    }
  };

  const selectedChapter = useMemo(
    () => chapters.find((chapter) => chapter._id === selectedChapterId) || null,
    [chapters, selectedChapterId],
  );

  return (
    <>
      <div dir={getLanguageDirection(i18n.language)} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">{t("teacherContent.videos_title", "Course videos")}</p>
              <p className="text-xs text-slate-500">
                {t("teacherContent.videos_description", "Add videos, reorder them, and open each one to update titles, descriptions, status, and uploaded video files.")}
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
              {t("teacherContent.video_count", { count: chapters.length, defaultValue_one: "{{count}} video", defaultValue_other: "{{count}} videos" })}
            </span>
          </div>

          {chapters.length ? (
            <div className="space-y-3">
              {chapters.map((chapter) => (
                <div
                  key={chapter._id}
                  draggable={!isOrdering}
                  onDragStart={() => handleDragStart(chapter._id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDrop(chapter._id)}
                  className={`rounded-2xl border bg-white p-4 shadow-sm ${
                    draggedChapterId === chapter._id
                      ? "border-indigo-300 ring-2 ring-indigo-100"
                      : "border-slate-200"
                  }`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <button
                        type="button"
                        disabled={isOrdering}
                        className="mt-0.5 cursor-grab rounded-lg border border-indigo-200 bg-white p-2 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-950 active:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                        aria-label={t(
                          "teacherContent.drag_video_aria",
                          "Drag to reorder video",
                        )}
                      >
                        <FaArrowsAlt size={12} />
                      </button>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {chapter?.title?.en || chapter?.title?.pr || chapter?.title?.ps || t("teacherContent.untitled_video", "Untitled video")}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {chapter?.video
                            ? t("teacherContent.video_uploaded", "Video uploaded")
                            : t(
                                "teacherContent.video_not_uploaded",
                                "Video file not uploaded yet",
                              )}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Chip
                        value={getStatusLabel(chapter?.status, t)}
                        variant="ghost"
                        size="sm"
                        color={statusTone(chapter?.status)}
                        className="text-xs capitalize"
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        onClick={() => setSelectedChapterId(chapter._id)}
                        className={`flex items-center gap-2 ${outlineButtonClass}`}
                      >
                        <FaEdit size={12} />
                        {t("teacherContent.edit_video", "Edit video")}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                <FaVideo size={16} />
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-900">{t("teacherContent.no_videos", "No videos added yet")}</p>
              <p className="mt-1 text-xs text-slate-500">
                {t("teacherContent.no_videos_description", "Start by creating the first video for this course. You can upload the file and refine its details right after.")}
              </p>
            </div>
          )}

          {isCreating ? (
            <form onSubmit={(event) => { event.preventDefault(); onCreateChapter(); }} className="rounded-2xl border border-indigo-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
                <div>
                  <div className="grid grid-cols-2 gap-3"><input type="number" min="0" max="99" value={lessonNumber} onChange={(e) => { const v=e.target.value; setLessonNumber(v); const titles=generateLessonTitles(v, partNumber); if (titles) setCreationTitles(titles); }} placeholder="Lesson Number" className="rounded-lg border p-2" /><input type="number" min="0" max="99" value={partNumber} onChange={(e) => { const v=e.target.value; setPartNumber(v); const titles=generateLessonTitles(lessonNumber, v); if (titles) setCreationTitles(titles); }} placeholder="Part Number" className="rounded-lg border p-2" /></div>
                  {['ps','pr','en'].map((lang) => <input key={lang} dir={lang === 'en' ? 'ltr' : 'rtl'} value={creationTitles[lang]} onChange={(e) => setCreationTitles((current) => ({ ...current, [lang]: e.target.value }))} placeholder={`${lang.toUpperCase()} title`} className="mt-2 w-full rounded-lg border p-2" />)}
                  {['ps','pr','en'].map((lang) => <textarea key={`${lang}-desc`} dir={lang === 'en' ? 'ltr' : 'rtl'} value={creationDescriptions[lang]} onChange={(event) => setCreationDescriptions((current) => ({ ...current, [lang]: event.target.value }))} placeholder={t("teacherContent.optional_description", { language: lang.toUpperCase(), defaultValue: `${lang.toUpperCase()} description (optional)` })} rows={2} className="mt-2 w-full rounded-lg border p-2" />)}
                  <label className="mt-3 block text-sm font-medium text-slate-700">{t("teacherContent.optional_lesson_file", "Lesson file (optional)")}</label>
                  <input type="file" accept=".pdf,.docx" onChange={(event) => setCreationFile(event.target.files?.[0] || null)} className="mt-1 block w-full text-sm text-slate-700" />
                  {errors.value ? (
                    <p className="mt-1 text-xs text-red-500">{errors.value.message}</p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">
                      {t("teacherContent.video_title_help", "Use a learner-friendly title. You can add more languages and the video file in the edit modal after creation.")}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:pt-7">
                  <Button type="submit" disabled={isSubmitting || isCreatingLesson} className={primaryButtonClass}>
                    {isSubmitting ? t("teacherContent.saving", "Saving...") : t("teacherContent.create_video", "Create video")}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="blue-gray"
                    onClick={() => {
                      reset({ value: "" });
                      setIsCreating(false);
                    }}
                  >
                    {t("teacherContent.cancel", "Cancel")}
                  </Button>
                </div>
              </div>
            </form>
          ) : null}

          <div className="flex justify-end border-t border-slate-200 pt-4">
            <Button
              type="button"
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 rounded-lg !border-indigo-700 !bg-indigo-700 !text-white hover:!bg-indigo-800 active:!bg-indigo-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 disabled:!bg-indigo-200 disabled:!text-indigo-800"
            >
              <FaPlusCircle size={14} />
              {t("teacherContent.add_video", "Add Video")}
            </Button>
          </div>
        </div>
      </div>

      <Modal open={Boolean(selectedChapter)} onClose={() => setSelectedChapterId("")} className="max-w-6xl">
        {selectedChapter ? (
          <>
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <p className="text-lg font-semibold text-slate-900">{t("teacherContent.edit_video_title", "Edit Video")}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {t("teacherContent.edit_video_description", "Update video details, file, and publishing state without leaving the content page.")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedChapterId("")}
                className="rounded-lg border border-indigo-200 bg-white p-2 text-indigo-700 transition hover:bg-indigo-50 hover:text-indigo-950 active:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
                aria-label={t("teacherContent.close_modal", "Close modal")}
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="max-h-[calc(88vh-88px)] overflow-y-auto px-6 py-5">
              <ChapterEditor
                courseId={courseId}
                chapter={selectedChapter}
                slug={slug}
                accessToken={accessToken}
                mutate={mutate}
                onClose={() => setSelectedChapterId("")}
              />
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
};

ChapterManager.propTypes = {
  courseId: PropTypes.string.isRequired,
  slug: PropTypes.string,
  chaptersList: PropTypes.arrayOf(PropTypes.object),
  accessToken: PropTypes.string,
  mutate: PropTypes.func.isRequired,
};

const TeacherCourseContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t, i18n } = useTranslation();
  const fetcher = (url) => fetchData(url, accessToken);
  const { data, error, isLoading, mutate } = useSWR(
    accessToken ? `${PROXY}/teacher/courses/${id}` : null,
    fetcher,
  );

  useEffect(() => {
    if (error) {
      toast.error(t("teacherContent.failed_load", "Failed to load course content."));
    }
  }, [error, t]);

  if (error) {
    return (
      <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-10 text-center">
          <Typography variant="h5" className="text-red-700">
            {t("teacherContent.cannot_manage_title", "You cannot manage this course")}
          </Typography>
          <Typography className="mt-2 text-red-600">
            {t(
              "teacherContent.cannot_manage_description",
              "The course was not found or you do not have permission to manage it.",
            )}
          </Typography>
          <div className="mt-5">
            <Button color="red" onClick={() => navigate("/teacher/courses")}>
              {t("teacherContent.back_to_studio", "Back to Teacher Studio")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-7xl space-y-4 px-4 pb-12 sm:px-6 lg:px-8">
        <div className="h-24 animate-pulse rounded-3xl border border-slate-200 bg-white" />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.9fr_1.5fr]">
          <div className="space-y-4">
            <div className="h-56 animate-pulse rounded-3xl border border-slate-200 bg-white" />
            <div className="h-64 animate-pulse rounded-3xl border border-slate-200 bg-white" />
          </div>
          <div className="h-[32rem] animate-pulse rounded-3xl border border-slate-200 bg-white" />
        </div>
      </section>
    );
  }

  if (data?.isLocked) {
    return <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-3xl px-4 pb-12"><div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900"><Typography variant="h4">{t("teacherContent.locked_title", "Course locked")}</Typography><Typography className="mt-3">{t("teacherContent.locked_message", "This course is read-only while it is locked by an administrator.")}</Typography><Link to="/teacher/courses" className="mt-5 inline-block"><Button variant="outlined" color="amber">{t("teacherContent.back_to_studio", "Back to Teacher Studio")}</Button></Link></div></section>;
  }

  return (
    <section dir={getLanguageDirection(i18n.language)} className="mx-auto mt-8 max-w-7xl space-y-4 px-4 pb-12 sm:px-6 lg:px-8">
      <Link
        to="/teacher/courses"
        className="inline-flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50 hover:text-indigo-950 active:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
      >
        <FaArrowLeft size={12} />
        <span>{t("teacherContent.back_to_studio", "Back to Teacher Studio")}</span>
      </Link>

      <div className={cardClassName}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Typography variant="h5" className="text-slate-900">
              {t("teacherContent.content_title", "Course Content")}
            </Typography>
            <Typography className="mt-1 text-slate-600">
              {t(
                "teacherContent.content_description",
                "Manage course videos separately from the main course details form so the workflow stays cleaner and easier to scan.",
              )}
            </Typography>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip
              value={getStatusLabel(data?.status, t)}
              color={data?.status === "published" ? "green" : "orange"}
              variant="ghost"
              className="capitalize"
            />
            <Link to={`/teacher/courses/${id}`}>
              <Button variant="outlined" className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold ${outlineButtonClass}`}>
                <FaEdit />
                {t("teacherContent.edit_details", "Edit details")}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.9fr_1.5fr]">
        <div className="space-y-4">
          <div className={cardClassName}>
            <p className="text-sm font-semibold text-slate-900">
              {t("teacherContent.current_course", "Current course")}
            </p>
            <p className="mt-2 text-sm text-slate-700">{getCourseTitle(data, t)}</p>
            <p className="mt-1 text-xs text-slate-500">
              {data?.slug || t("teacherContent.no_slug", "No slug")}
            </p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <p>
                <span className="font-semibold text-slate-900">{data?.chapters?.length || 0}</span>{" "}
                {t("teacherContent.video_count", {
                  count: data?.chapters?.length || 0,
                  defaultValue_one: "{{count}} video",
                  defaultValue_other: "{{count}} videos",
                }).replace(`${data?.chapters?.length || 0} `, "")}
              </p>
              <p>
                <span className="font-semibold text-slate-900">
                  {data?.duration || t("teacherContent.not_set", "Not set")}
                </span>{" "}
                {t("teacherContent.duration_suffix", "duration")}
              </p>
              <p>
                <span className="font-semibold capitalize text-slate-900">
                  {data?.videoLang || t("teacherContent.not_set", "Not set")}
                </span>{" "}
                {t("teacherContent.video_language_suffix", "video language")}
              </p>
            </div>
          </div>

          <div className={cardClassName}>
            <div className="flex items-start gap-3">
              <span className="mt-1 rounded-full bg-indigo-100 p-2 text-indigo-600">
                <FaPlayCircle size={14} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {t("teacherContent.workflow_title", "Workflow")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t(
                    "teacherContent.workflow_description",
                    "Keep course details on the edit page and manage the course video list here. This avoids mixing metadata work with upload and ordering tasks.",
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className={cardClassName}>
            <div className="flex items-start gap-3">
              <span className="mt-1 rounded-full bg-emerald-100 p-2 text-emerald-600">
                <FaVideo size={14} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {t("teacherContent.recommended_flow_title", "Recommended flow")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t("teacherContent.recommended_flow_step_1", "1. Add a video at the bottom of the list.")}
                </p>
                <p className="text-xs text-slate-500">
                  {t("teacherContent.recommended_flow_step_2", "2. Open the modal and upload the file.")}
                </p>
                <p className="text-xs text-slate-500">
                  {t("teacherContent.recommended_flow_step_3", "3. Publish the video when it is ready.")}
                </p>
              </div>
            </div>
          </div>

          <div className={cardClassName}>
            <div className="mb-4">
              <p className="text-sm font-semibold text-slate-900">
                {t("teacherContent.course_files_title", "Course files")}
              </p>
              <p className="text-xs text-slate-500">
                {t(
                  "teacherContent.course_files_description",
                  "Upload and manage course documents separately from videos so learners can find supporting materials easily.",
                )}
              </p>
            </div>
            <CourseFilesManager
              courseId={id}
              files={data?.files || []}
              accessToken={accessToken}
              mutate={mutate}
            />
          </div>
        </div>

        <div className={cardClassName}>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {t("teacherContent.videos_title", "Videos")}
              </p>
              <p className="text-xs text-slate-500">
                {t(
                  "teacherContent.videos_description",
                  "Create videos, reorder them, and open each one to update titles, descriptions, upload the file, and manage status.",
                )}
              </p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              {t("teacherContent.video_count", {
                count: data?.chapters?.length || 0,
                defaultValue_one: "{{count}} video",
                defaultValue_other: "{{count}} videos",
              })}
            </span>
          </div>
          <ChapterManager
            courseId={id}
            slug={data?.slug}
            chaptersList={data?.chapters || []}
            accessToken={accessToken}
            mutate={mutate}
          />
        </div>
      </div>
    </section>
  );
};

export default TeacherCourseContent;
